import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import defaultTo from 'lodash/defaultTo';
import debounce from 'lodash/debounce';
import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import Checkbox from '@atlaskit/checkbox';
import { N800, G500 } from '@atlaskit/theme/colors';
import { Box } from '@atlaskit/primitives';
import Select, { ValueType } from '@atlaskit/select';
import { ErrorMessage, Field, Fieldset } from '@atlaskit/form';
import EditorSuccessIcon from '@atlaskit/icon/glyph/editor/success';
import Spinner from '@atlaskit/spinner';
import isEmail from 'validator/lib/isEmail';
import Textfield from '@atlaskit/textfield';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { createAtlassianAccountWithNameAndEmail } from '@atlassian/temp-proxies';
import { validateEmailByAtlassianAccount } from './add-contact-fields';
import { SuccessBanner } from './success-banner';
import { SignUpBanner } from './sign-up-banner';
import './add-contact-select-field.scss';

export interface AvailableOptionsProp {
  fullName?: string;
  email?: string;
  isPartnerAdmin?: boolean;
  picture?: string;
  atlassianAccountId?: string;
  identifierId?: string;
}
interface AddContactSelectFieldsProps {
  atlassianAccountIds: Array<string>;
  setAtlassianAccountIds: Dispatch<SetStateAction<Array<string>>>;
  atlassianAccountEmails: Array<string>;
  setAtlassianAccountEmails: Dispatch<SetStateAction<Array<string>>>;
  currentUserList?: Array<string>;
  userAlreadyExistText?: string;
  emailFieldText: string;
  addButtonText?: string;
  validateEmailField?: (_email: string) => string | undefined;
  isAddNewContactDisabled?: boolean;
  isExistingContactRemoveDisabled?: boolean;
  removableCustomerEmailList?: string[];
  fullNameFieldLabel?: string;
  emailFieldLabel?: string;
  version?: number;
  fieldType?: string;
  availableOptions?: AvailableOptionsProp[];
  availableOptionsLoading?: boolean;
  isRequired?: boolean;
  shouldAllowAddingContact?: boolean;
  textFieldLabel?: string;
  nameFieldText?: string;
  isRemoveButtonHidden?: boolean;
  isSetAsPrimaryEnabled?: boolean;
  setIsSetAsPrimaryChecked?: Dispatch<SetStateAction<boolean>>;
}

enum AtlassianAccountStatus {
  FETCHED = 'FETCHED',
  FETCHING = 'FETCHING',
  NOT_FETCHED = 'NOT_FETCHED',
  CREATED = 'CREATED',
}

enum Options {
  Add_CONTACT_OPTION = 'Add contact',
}

const DEFAULT_CONTACT_OBJECT = {
  email: '',
  name: '',
  atlassianAccountStatus: AtlassianAccountStatus.NOT_FETCHED,
  error: '',
  isEditable: true,
  nameError: false,
  removeDisabled: false,
};

interface Contact {
  email: string;
  name: string;
  atlassianAccountStatus: AtlassianAccountStatus;
  error: string;
  isEditable: boolean;
  nameError: boolean;
  removeDisabled: boolean;
}

interface BillingAdminSelectItem {
  label: React.ReactNode;
  value: string;
  email: string;
  id: string;
  name: string;
}

const SelectWrapper = styled.div({
  '& .quote-contact-select__control': {
    backgroundColor: '#fff',
    borderColor: '#fff',
  },
  '& .quote-contact-select__control:hover': {
    backgroundColor: '#F4F5F7',
    borderColor: '#F4F5F7',
  },
});

const inputBox = css({
  border: 0,
  background: 'transparent',
  color: N800,
  minWidth: '340px',
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

const BillingAdminSelectOption = ({ billingAdmin }: { billingAdmin: AvailableOptionsProp | null | undefined }) => {
  return (
    <section
      className="dropdown-option billing-admin-person-container"
      key={`select-option-${billingAdmin?.atlassianAccountId}`}
    >
      <Avatar src={billingAdmin?.picture} size="medium" />
      <div className="billing-admin-person">
        <span>{billingAdmin?.fullName ?? 'No name'}</span>
        <span data-testid={`billing-admin-email-${billingAdmin?.email}`} className="billing-admin-person-email">
          {billingAdmin?.email}
        </span>
      </div>
    </section>
  );
};

const AddContactLabel = () => {
  return (
    <section className="dropdown-option billing-admin-person-container" key={`select-option-add-contact`}>
      <AddIcon label="Add icon" size="small" />
      <div className="billing-admin-person">
        <span>Add new contact</span>
      </div>
    </section>
  );
};

const getDefaultUserList = (
  atlassianAccountEmails: Array<string>,
  removeDisabled: boolean,
  removableCustomerEmailList?: string[]
) => {
  return atlassianAccountEmails?.map((email) => {
    const isRemoveDisabled = (function () {
      // If removableCustomerEmailList is provided, then removeDisabled will be true for the emails present in the list
      if (removableCustomerEmailList && removableCustomerEmailList.length > 0) {
        return !removableCustomerEmailList.includes(email);
      }
      return removeDisabled;
    })();
    return {
      email,
      name: '',
      atlassianAccountStatus: email ? AtlassianAccountStatus.FETCHED : AtlassianAccountStatus.NOT_FETCHED,
      error: '',
      isEditable: !email,
      nameError: false,
      removeDisabled: isRemoveDisabled,
    };
  });
};

const VALIDATE_EMAIL_INPUT_DELAY = 600;
const VALIDATE_ATLASSIAN_EMAIL_DEBOUNCE_TIME = 1000;

// this need to refactor to use new design of dropdown
export const AddContactSelectFields = ({
  atlassianAccountIds,
  setAtlassianAccountIds,
  atlassianAccountEmails,
  setAtlassianAccountEmails,
  userAlreadyExistText = '',
  emailFieldText,
  addButtonText,
  isAddNewContactDisabled = false,
  isExistingContactRemoveDisabled = false,
  removableCustomerEmailList = [],
  fieldType,
  availableOptions,
  availableOptionsLoading,
  currentUserList = [],
  shouldAllowAddingContact = false,
  textFieldLabel,
  nameFieldText,
  isRemoveButtonHidden = false,
  isSetAsPrimaryEnabled,
  setIsSetAsPrimaryChecked,
}: AddContactSelectFieldsProps) => {
  const [userList, setUserList] = useState<Array<Contact>>(
    getDefaultUserList(atlassianAccountEmails, isExistingContactRemoveDisabled, removableCustomerEmailList)
  );

  const [isChecked, setIsChecked] = useState(false);

  const [isAddingContact, setIsAddingContact] = useState(false);

  const fieldName = fieldType ?? 'customer-contact';

  const addNewContact = useCallback(() => {
    const clonedUserList = cloneDeep(userList);
    clonedUserList[clonedUserList.length - 1] = { ...clonedUserList[clonedUserList.length - 1], isEditable: false };
    setUserList([...clonedUserList, { ...DEFAULT_CONTACT_OBJECT }]);
    setAtlassianAccountIds([...atlassianAccountIds, '']);
    setAtlassianAccountEmails([...atlassianAccountEmails, '']);
  }, [atlassianAccountIds, setAtlassianAccountIds, atlassianAccountEmails, setAtlassianAccountEmails, userList]);

  const removeCustomerContact = useCallback(
    (idx) => {
      const newUserList = userList.length === 1 ? [] : [...userList.slice(0, idx), ...userList.slice(idx + 1)];
      if (userList.length > 1 && idx === userList.length - 1 && !newUserList[idx - 1].removeDisabled) {
        newUserList[idx - 1].isEditable = true;
      }
      setUserList(userList.length === 1 ? [DEFAULT_CONTACT_OBJECT] : newUserList);
      const newIds = [...atlassianAccountIds.slice(0, idx), ...atlassianAccountIds.slice(idx + 1)];
      const newEmails = [...atlassianAccountEmails.slice(0, idx), ...atlassianAccountEmails.slice(idx + 1)];
      setAtlassianAccountIds(userList.length === 1 ? [''] : newIds);
      setAtlassianAccountEmails(userList.length === 1 ? [''] : newEmails);
    },
    [atlassianAccountIds, setAtlassianAccountIds, atlassianAccountEmails, setAtlassianAccountEmails, userList]
  );

  const removeAddedContactButton = useCallback(() => {
    setUserList([DEFAULT_CONTACT_OBJECT]);
    setAtlassianAccountIds(['']);
    setAtlassianAccountEmails(['']);
  }, [atlassianAccountIds, setAtlassianAccountIds, atlassianAccountEmails, setAtlassianAccountEmails, userList]);

  const handleBillingAdminUpdate = useCallback(
    (selectedBillingAdmin: BillingAdminSelectItem | undefined | null, idx: number) => {
      if (!selectedBillingAdmin) {
        return null;
      }
      let hasFieldError = false;
      const newUserList = cloneDeep(userList);
      if (selectedBillingAdmin?.name === Options.Add_CONTACT_OPTION) {
        newUserList[idx].email = '';
        newUserList[idx].name = Options.Add_CONTACT_OPTION;
        newUserList[idx].error = '';
        newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.FETCHED;
        setUserList(newUserList);
        setAtlassianAccountIds(['']);
        setAtlassianAccountEmails(['']);
      } else {
        newUserList[idx].email = defaultTo(selectedBillingAdmin?.email, '');
        newUserList[idx].name = defaultTo(selectedBillingAdmin?.name, '');
        newUserList[idx].error = '';
        newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.FETCHED;

        const selectedAdminInCurrentUserList = userList?.filter((user) => user.email === selectedBillingAdmin.email);
        if (
          selectedBillingAdmin?.id &&
          selectedAdminInCurrentUserList.length > 0 &&
          !selectedAdminInCurrentUserList?.[0]?.isEditable
        ) {
          newUserList[idx].error = userAlreadyExistText;
          hasFieldError = true;
        }
        setUserList(newUserList);
        if (hasFieldError) {
          return null;
        }
        setAtlassianAccountIds((prevValue) => {
          const clonedValue = cloneDeep(prevValue);
          clonedValue[idx] = selectedBillingAdmin?.id;
          return clonedValue;
        });
        setAtlassianAccountEmails((prevValue) => {
          const clonedValue = cloneDeep(prevValue);
          clonedValue[idx] = selectedBillingAdmin?.email;
          return clonedValue;
        });
      }
    },
    [setAtlassianAccountIds, setAtlassianAccountEmails, userList, userAlreadyExistText]
  );

  const getRemoveAddedContactButton = useCallback(
    (idx) => {
      return (
        <Button
          appearance="link"
          testId={`remove-button-${idx}`}
          onClick={() => removeAddedContactButton()}
          className="remove-button"
        >
          Remove
        </Button>
      );
    },
    [removeCustomerContact]
  );

  const getRemoveContactButton = useCallback(
    (idx) => {
      return (
        <Button
          appearance="link"
          testId={`remove-button-${idx}`}
          onClick={() => removeCustomerContact(idx)}
          className="remove-button"
        >
          Remove
        </Button>
      );
    },
    [removeCustomerContact]
  );

  const availableBillingAdminOptions = defaultTo(availableOptions, [])
    ?.filter((billingAdmin) => billingAdmin?.email && !atlassianAccountEmails?.includes(billingAdmin.email))
    ?.map((billingAdmin: AvailableOptionsProp) => {
      return {
        label: <BillingAdminSelectOption billingAdmin={billingAdmin} />,
        value: defaultTo(billingAdmin?.fullName, ''),
        email: defaultTo(billingAdmin?.email, ''),
        id: defaultTo(billingAdmin?.atlassianAccountId, ''),
        name: defaultTo(billingAdmin?.fullName, ''),
      };
    }) as BillingAdminSelectItem[];

  const addContactOption = {
    label: <AddContactLabel />,
    value: 'add-new-contact',
    email: '',
    id: '',
    name: 'Add new contact',
  };

  const getDefaultSelectedValue = (idx: number) => {
    const selectedBillingAdmin = defaultTo(availableOptions, []).find(
      (billingAdmin) => billingAdmin?.email === userList?.[idx]?.email
    );
    if (!selectedBillingAdmin?.email) return null;
    return {
      label: <BillingAdminSelectOption billingAdmin={selectedBillingAdmin} />,
      value: defaultTo(selectedBillingAdmin?.fullName, ''),
      email: defaultTo(selectedBillingAdmin?.email, ''),
      id: defaultTo(selectedBillingAdmin?.atlassianAccountId, ''),
      name: defaultTo(selectedBillingAdmin?.fullName, ''),
    };
  };

  const validateEmail = useCallback((email: string | undefined) => {
    if (!email) return 'Email address is required'; // if there is no input
    if (!isEmail(email)) return 'Enter a valid email address'; // if the input is not a valid email address
  }, []);

  const delayedValidateEmail = useCallback(
    async (email: string | undefined) => {
      return new Promise((resolve) => setTimeout(resolve, VALIDATE_EMAIL_INPUT_DELAY)).then(() => {
        return validateEmail(email);
      });
    },
    [validateEmail]
  );

  const validateEmailByAtlassianAccountCallback = useCallback(
    async (email: string, newUserList: Array<Contact>, idx: number, currentUserList: Array<string>) => {
      if (!validateEmail(email)) {
        const res = await validateEmailByAtlassianAccount(email, currentUserList, userAlreadyExistText);
        const clonedUserList = cloneDeep(newUserList);
        clonedUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.FETCHED;
        clonedUserList[idx].error = res.error;
        setAtlassianAccountIds((prevValue) => {
          const clonedValue = cloneDeep(prevValue);
          clonedValue[idx] = res.id;
          return clonedValue;
        });
        setAtlassianAccountEmails((prevValue) => {
          const clonedValue = cloneDeep(prevValue);
          clonedValue[idx] = email;
          return clonedValue;
        });
        setUserList(clonedUserList);
      }
    },
    [validateEmail, userAlreadyExistText, setAtlassianAccountIds, setAtlassianAccountEmails]
  );

  const debouncedValidateEmailByAtlassian = useMemo(
    () => debounce(validateEmailByAtlassianAccountCallback, VALIDATE_ATLASSIAN_EMAIL_DEBOUNCE_TIME),
    [validateEmailByAtlassianAccountCallback]
  );

  const handleEmailUpdate = useCallback(
    (email: string, idx: number) => {
      const newUserList = cloneDeep(userList);
      newUserList[idx].email = email;
      newUserList[idx].name = '';
      newUserList[idx].error = '';
      newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.NOT_FETCHED;
      if (!validateEmail(email)) {
        newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.FETCHING;
      }
      debouncedValidateEmailByAtlassian(email, newUserList, idx, currentUserList);
      setUserList(newUserList);
      setAtlassianAccountIds((prevValue) => {
        const clonedValue = cloneDeep(prevValue);
        clonedValue[idx] = '';
        return clonedValue;
      });
      setAtlassianAccountEmails((prevValue) => {
        const clonedValue = cloneDeep(prevValue);
        clonedValue[idx] = email;
        return clonedValue;
      });
    },
    [debouncedValidateEmailByAtlassian, setAtlassianAccountIds, setAtlassianAccountEmails, userList, validateEmail]
  );

  const getCreateAtlassianAccountSuccessBanner = useCallback(
    (idx: number) => {
      if (userList[idx].atlassianAccountStatus === AtlassianAccountStatus.CREATED) {
        return <SuccessBanner email={userList[idx].email} />;
      }
    },
    [userList]
  );

  const createAtlassianAccount = useCallback(
    async (idx: number) => {
      const newUserList = cloneDeep(userList);
      try {
        if (userList[idx].name) {
          const res = await createAtlassianAccountWithNameAndEmail(userList[idx].email, userList[idx].name, 'wac');
          newUserList[idx].atlassianAccountStatus = AtlassianAccountStatus.CREATED;
          const clonedAtlassianAccountIds = cloneDeep(atlassianAccountIds);
          clonedAtlassianAccountIds[idx] = res.id;
          setAtlassianAccountIds(clonedAtlassianAccountIds);
          const clonedAtlassianAccountEmails = cloneDeep(atlassianAccountEmails);
          clonedAtlassianAccountEmails[idx] = userList[idx].email;
          setAtlassianAccountEmails(clonedAtlassianAccountEmails);
        } else {
          newUserList[idx].nameError = true;
        }
      } catch (error) {
        newUserList[
          idx
        ].error = `We couldn't create an Atlassian account for ${userList[idx].email}. Try again or contact Partner Service Desk`;
      }
      setUserList(newUserList);
    },
    [userList, atlassianAccountIds, setAtlassianAccountIds, atlassianAccountEmails, setAtlassianAccountEmails]
  );

  const getAccountSignUpBanner = useCallback(
    (idx: number) => {
      if (
        !atlassianAccountIds[idx] &&
        userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHED &&
        isEmail(userList[idx].email) &&
        !userList[idx].error
      ) {
        return (
          <SignUpBanner email={userList[idx].email} handleCreateAtlassianAccount={() => createAtlassianAccount(idx)} />
        );
      }
    },
    [atlassianAccountIds, createAtlassianAccount, userList]
  );

  const getNameField = useCallback(
    (idx: number) => {
      if (
        !atlassianAccountIds[idx] &&
        userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHED &&
        isEmail(userList[idx].email) &&
        !userList[idx].error
      ) {
        return (
          <Field name={`customer-contact-name[${idx}]`} isRequired label={'Enter full name'}>
            {({ fieldProps }) => (
              <>
                <Textfield
                  {...fieldProps}
                  testId={`customer-contact-name-${idx}`}
                  autoComplete="off"
                  placeholder={nameFieldText}
                  value={userList[idx].name}
                  onChange={(event: React.FormEvent<HTMLInputElement>) => {
                    const newUserList = cloneDeep(userList);
                    newUserList[idx].name = event.currentTarget.value;
                    newUserList[idx].nameError = !event.currentTarget.value;
                    setUserList(newUserList);
                    fieldProps.onChange(event);
                  }}
                />
                {userList[idx].nameError && <ErrorMessage>{'Full name is required'}</ErrorMessage>}
              </>
            )}
          </Field>
        );
      }
    },
    [atlassianAccountIds, userList]
  );

  const getElementAfterEmailInput = useCallback(
    (idx) => {
      if (userList[idx].atlassianAccountStatus === AtlassianAccountStatus.FETCHING) {
        return (
          <div className="input-after-icon">
            <Spinner size="small" />
          </div>
        );
      } else if (atlassianAccountIds[idx]) {
        return (
          <div className="input-after-icon">
            <EditorSuccessIcon size="medium" primaryColor={G500} label="" />
          </div>
        );
      }
      return null;
    },
    [atlassianAccountIds, userList]
  );

  const handleSelectChange = (selectedItem: ValueType<BillingAdminSelectItem, false>, idx: number) => {
    if (selectedItem && (selectedItem as BillingAdminSelectItem).value === 'add-new-contact') {
      setIsAddingContact(true);
      handleBillingAdminUpdate(selectedItem, idx);
    } else {
      setIsAddingContact(false);
      handleBillingAdminUpdate(selectedItem, idx);
    }
  };

  const getCustomerContactInputField = (idx: number) => {
    return (
      <Fieldset key={idx}>
        <div className="contact-field">
          <div>
            <Field
              name={`customer-contact-email[${idx}]`}
              validate={(email) => delayedValidateEmail(email)}
              label={'Customer contact email'}
              isRequired
            >
              {({ fieldProps, error }) => {
                return (
                  <>
                    <div className="add-contact">
                      <Textfield
                        {...fieldProps}
                        testId={`customer-contact-email-${idx}`}
                        value={userList[idx].email}
                        autoComplete="off"
                        placeholder={textFieldLabel}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                          handleEmailUpdate(event.currentTarget.value, idx);
                          fieldProps.onChange(event);
                        }}
                        elemAfterInput={getElementAfterEmailInput(idx)}
                      />
                    </div>
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                  </>
                );
              }}
            </Field>
            {getNameField(idx)}
            {getAccountSignUpBanner(idx)}
            {getCreateAtlassianAccountSuccessBanner(idx)}
          </div>
          {!isRemoveButtonHidden && idx >= 0 && getRemoveAddedContactButton(idx)}
        </div>
      </Fieldset>
    );
  };

  const isAddBillingAdminDisabled =
    atlassianAccountIds?.findIndex((id) => !id) >= 0 || userList[userList.length - 1]?.error?.length > 0;
  const getAvailableOptions = () => {
    if (shouldAllowAddingContact) {
      return [addContactOption, ...availableBillingAdminOptions];
    }
    return [...availableBillingAdminOptions];
  };

  React.useEffect(() => {
    if (isSetAsPrimaryEnabled) {
      setIsSetAsPrimaryChecked && setIsSetAsPrimaryChecked(isChecked);
    }
  }, [isChecked]);

  return (
    <div className="add-contact-container" data-testid="contacts-select-dropdown">
      {userList.map((user, idx) => {
        if (user.isEditable) {
          return (
            <Fieldset key={idx}>
              <div className="contact-field">
                <div>
                  <Field<ValueType<BillingAdminSelectItem>, HTMLSelectElement>
                    name={`${fieldName}-select[${idx}]`}
                    css={inputBox}
                  >
                    {({ fieldProps, error }) => (
                      <SelectWrapper className="action" data-testid="billing-admin">
                        <Select<BillingAdminSelectItem | null>
                          {...fieldProps}
                          options={getAvailableOptions()}
                          data-testid="billing-admin-select-field"
                          appearance="subtle"
                          classNamePrefix="billing-admin-select"
                          isLoading={availableOptionsLoading}
                          value={getDefaultSelectedValue(idx)}
                          onChange={(selectedItem: ValueType<BillingAdminSelectItem, false>) => {
                            handleSelectChange(selectedItem, idx);
                            fieldProps.onChange(selectedItem);
                          }}
                          isSearchable={true}
                          placeholder={emailFieldText}
                        />
                        {error && <ErrorMessage>{error}</ErrorMessage>}
                      </SelectWrapper>
                    )}
                  </Field>
                  {isAddingContact && getCustomerContactInputField(idx)}
                  {user.error && <ErrorMessage testId={`error-in-fetching-${idx}`}>{user.error}</ErrorMessage>}
                </div>
                {!isAddingContact && !isRemoveButtonHidden && idx >= 0 && getRemoveContactButton(idx)}
              </div>
            </Fieldset>
          );
        } else {
          return (
            <div
              key={idx}
              className={classNames({ 'user-info-section': true, 'spacing-top': shouldAllowAddingContact })}
            >
              <div className="user-email" data-testId="user-email">
                {user.email}
              </div>
              {!user.removeDisabled && atlassianAccountIds.length >= 1 && getRemoveContactButton(idx)}
            </div>
          );
        }
      })}
      {!isAddNewContactDisabled && (
        <div className="btn-add-another-email-address">
          <Button
            appearance="link"
            testId="add-new-customer-contact"
            iconBefore={<AddIcon label="Add icon" size="small" />}
            onClick={addNewContact}
            isDisabled={isAddBillingAdminDisabled}
          >
            {addButtonText}
          </Button>
        </div>
      )}
      {isSetAsPrimaryEnabled && (
        <Box paddingBlockStart="space.100">
          <Checkbox
            value="default checkbox"
            isChecked={isChecked}
            label="Set as key contact"
            onChange={() => {
              setIsChecked((current) => !current);
              setUserList((prevUserList) => {
                const updatedUserList = [...prevUserList];
                updatedUserList[0] = { ...updatedUserList[0], error: '' };
                return updatedUserList;
              });
            }}
            name="checkbox-default"
            testId="set-primary-checkbox"
          />
        </Box>
      )}
    </div>
  );
};
